/**
 * Leader names, resolved server-side by LocationManagement (GET api/location-leaders/view): the
 * shared Member cache first, then MemberManagement's API. So names show for anyone with
 * Location.Leader.View, without the Member.Read right. One small request covers every campus
 * (leaders are a few dozen rows), shared by the dashboard, campus list and leaders pages.
 */

import { useQuery } from '@tanstack/react-query';
import { useLocationLeaderService, type LocationLeaderView } from '@actschurch/ccms-sdk';
import { locationsKeys } from './locationsKeys';

export interface ResolvedLeader {
  memberId: number;
  /** Display name, or "Member #123" when it could not be resolved. */
  name: string;
  email?: string | null;
  cellNumber?: string | null;
  avatar?: string | null;
  /** Null when unknown. */
  isActive: boolean | null;
  /** The details were resolved. */
  found: boolean;
  /** The member record does not exist any more (deleted or merged). */
  missing: boolean;
  loading: boolean;
}

export function leaderDisplayName(view: Pick<LocationLeaderView, 'memberID' | 'memberName' | 'email'> | null | undefined, memberId: number): string {
  return view?.memberName?.trim() || view?.email?.trim() || `Member #${memberId}`;
}

export function resolveLeaderView(view: LocationLeaderView | undefined, memberId: number, loading = false): ResolvedLeader {
  return {
    memberId,
    name: leaderDisplayName(view, memberId),
    email: view?.email ?? null,
    cellNumber: view?.cellNumber ?? null,
    avatar: view?.avatar ?? null,
    isActive: view?.memberIsActive ?? null,
    found: !!view?.memberFound,
    missing: !!view?.memberMissing,
    loading,
  };
}

/** Names for leaders on every campus. Pass `enabled = perms.canViewLeaders`. */
export function useLeaderNames(enabled: boolean) {
  const leaderService = useLocationLeaderService();
  const query = useQuery({
    queryKey: locationsKeys.allLeaderViews,
    queryFn: () => leaderService.getAllViews(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const byMember = new Map<number, LocationLeaderView>();
  for (const v of query.data ?? []) if (!byMember.has(v.memberID)) byMember.set(v.memberID, v);

  const resolve = (memberId: number): ResolvedLeader => resolveLeaderView(byMember.get(memberId), memberId, enabled && query.isLoading);

  return {
    /** Every leader row (all campuses) with member details. */
    views: query.data ?? [],
    resolve,
    nameOf: (memberId: number) => resolve(memberId).name,
    isLoading: enabled && query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
  };
}
